import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import type { ChartRenderProps } from '../../lib/charts';
import { useDashboardStore } from '../../state/dashboardStore';

export default function MarkdownCard({ widget, dashboardId }: ChartRenderProps) {
  const updateWidget = useDashboardStore((s) => s.updateWidget);
  const content = widget.content ?? '';
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(content);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!editing) setDraft(content);
  }, [content, editing]);

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [editing]);

  const save = () => {
    updateWidget(dashboardId, widget.id, { content: draft });
    setEditing(false);
  };

  const cancel = () => {
    setDraft(content);
    setEditing(false);
  };

  if (editing) {
    return (
      <div
        className="chart-markdown chart-markdown--editing"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <textarea
          ref={textareaRef}
          className="chart-markdown__textarea"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') cancel();
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') save();
          }}
          placeholder="Write markdown… (# heading, **bold**, *italic*, `code`, [link](url))"
        />
        <div className="chart-markdown__actions">
          <button
            type="button"
            className="chart-markdown__btn"
            onClick={cancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="chart-markdown__btn chart-markdown__btn--primary"
            onClick={save}
          >
            Save
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="chart-markdown"
      onDoubleClick={() => setEditing(true)}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {content.trim() ? (
        <div className="chart-markdown__view">{renderMarkdown(content)}</div>
      ) : (
        <p className="chart-markdown__empty">
          Empty note. Click <em>Edit</em> to add markdown.
        </p>
      )}
      <button
        type="button"
        className="chart-markdown__edit"
        onClick={() => setEditing(true)}
        aria-label="Edit markdown"
      >
        Edit
      </button>
    </div>
  );
}

function renderMarkdown(source: string): ReactNode {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === '') {
      i++;
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      const level = heading[1].length;
      const text = heading[2];
      const Tag = `h${Math.min(level + 2, 6)}` as 'h3' | 'h4' | 'h5' | 'h6';
      blocks.push(<Tag key={key++}>{renderInline(text)}</Tag>);
      i++;
      continue;
    }

    if (/^\s*[-*]\s+/.test(line)) {
      const items: ReactNode[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        const item = lines[i].replace(/^\s*[-*]\s+/, '');
        items.push(<li key={key++}>{renderInline(item)}</li>);
        i++;
      }
      blocks.push(<ul key={key++}>{items}</ul>);
      continue;
    }

    if (/^\s*\d+\.\s+/.test(line)) {
      const items: ReactNode[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        const item = lines[i].replace(/^\s*\d+\.\s+/, '');
        items.push(<li key={key++}>{renderInline(item)}</li>);
        i++;
      }
      blocks.push(<ol key={key++}>{items}</ol>);
      continue;
    }

    const paraLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^(#{1,6})\s+/.test(lines[i]) &&
      !/^\s*[-*]\s+/.test(lines[i]) &&
      !/^\s*\d+\.\s+/.test(lines[i])
    ) {
      paraLines.push(lines[i]);
      i++;
    }
    blocks.push(
      <p key={key++}>
        {paraLines.map((l, idx) => (
          <Fragment key={idx}>
            {idx > 0 && <br />}
            {renderInline(l)}
          </Fragment>
        ))}
      </p>,
    );
  }

  return blocks;
}

function renderInline(text: string): ReactNode {
  const nodes: ReactNode[] = [];
  let rest = text;
  let key = 0;

  const patterns: Array<{
    re: RegExp;
    wrap: (match: RegExpExecArray) => ReactNode;
  }> = [
    {
      re: /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/,
      wrap: (m) => (
        <a key={key++} href={m[2]} target="_blank" rel="noreferrer noopener">
          {m[1]}
        </a>
      ),
    },
    {
      re: /\*\*([^*]+)\*\*/,
      wrap: (m) => <strong key={key++}>{m[1]}</strong>,
    },
    {
      re: /\*([^*]+)\*/,
      wrap: (m) => <em key={key++}>{m[1]}</em>,
    },
    {
      re: /`([^`]+)`/,
      wrap: (m) => <code key={key++}>{m[1]}</code>,
    },
  ];

  while (rest.length > 0) {
    let earliest: { idx: number; len: number; node: ReactNode } | null = null;
    for (const p of patterns) {
      const m = p.re.exec(rest);
      if (m && m.index !== undefined) {
        if (earliest === null || m.index < earliest.idx) {
          earliest = { idx: m.index, len: m[0].length, node: p.wrap(m) };
        }
      }
    }
    if (!earliest) {
      nodes.push(rest);
      break;
    }
    if (earliest.idx > 0) {
      nodes.push(rest.slice(0, earliest.idx));
    }
    nodes.push(earliest.node);
    rest = rest.slice(earliest.idx + earliest.len);
  }

  return nodes;
}
